import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, unref as _unref, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = { class: "ev-reports-container" }
const _hoisted_2 = { class: "reports-header" }
const _hoisted_3 = { class: "reports-stats" }
const _hoisted_4 = {
  key: 1,
  class: "loading-container"
}
const _hoisted_5 = {
  key: 2,
  class: "reports-content"
}
const _hoisted_6 = {
  key: 0,
  class: "reports-section"
}
const _hoisted_7 = { class: "section-title" }
const _hoisted_8 = { class: "img-list" }
const _hoisted_9 = {
  key: 0,
  class: "more-images"
}
const _hoisted_10 = {
  key: 1,
  class: "reports-section"
}
const _hoisted_11 = { class: "section-title" }
const _hoisted_12 = { class: "img-list" }
const _hoisted_13 = {
  key: 0,
  class: "more-images"
}
const _hoisted_14 = {
  key: 3,
  class: "pagination-container"
}
const _hoisted_15 = {
  key: 4,
  class: "no-data"
}

import { ref, onMounted, computed, inject } from 'vue'
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'

import {
  getEvReports,
  changeStatusReport,
  getEvCarProfileById,
  type DamageReport,
  type EvReportsResponse,
  type EvReportsMeta,
  type GetEvReportsParams
} from '@/api/car-ev-data'
import { getUserDetails } from '@/api/user'
import { routeNames } from '@/router/route-names'
import CommonError from '@/components/CommonError.vue'

// State

export default /*@__PURE__*/_defineComponent({
  __name: 'EVReports',
  setup(__props) {

const loading = ref(false)
const error = ref<string | null>(null)
const reportsData = ref<EvReportsResponse | null>(null)
const users = ref<Record<string, any>>({})
const vehicles = ref<Record<string, any>>({})
const resolvingReports = ref<string[]>([])

// Pagination state
const currentPage = ref(1)
const perPage = ref(20)
const totalReports = computed(() => reportsData.value?.meta?.total || 0)
const totalPages = computed(() => {
  if (!reportsData.value?.meta?.total) return 0
  return Math.ceil(reportsData.value.meta.total / perPage.value)
})

// Computed properties
const meta = computed<EvReportsMeta | null>(() => reportsData.value?.meta || null)
const reports = computed<DamageReport[]>(() => {
  if (!reportsData.value?.data) return []
  return [...reportsData.value.data.active, ...reportsData.value.data.resolved]
})
const activeReports = computed<DamageReport[]>(() => reportsData.value?.data.active || [])
const resolvedReports = computed<DamageReport[]>(() => reportsData.value?.data.resolved || [])

// Load reports
const loadReports = async (page: number = currentPage.value) => {
  try {
    loading.value = true
    error.value = null

    const params: GetEvReportsParams = {
      include: ['user', 'vehicle'], // спробуй покладатись на це
      page,
      perPage: perPage.value,
      sortBy: 'created_at',
      sortDirection: 'desc'
    }

    const response = await getEvReports(params)
    reportsData.value = response

    // Extract included data to avoid N+1 requests
    if (response.data) {
      const allReports = [
        ...response.data.active,
        ...response.data.resolved
      ]

      allReports.forEach((report) => {
        if (report.user && report.user_id) {
          users.value[report.user_id] = report.user
        }
        if (report.vehicle && report.ev_car_profile_id) {
          vehicles.value[report.ev_car_profile_id] = report.vehicle
        }
      })
    }

    // НЕ блокуємо UI
    // фоново підвантажуємо те, чого не вистачає
    loadRelatedData().catch((e) => console.error('loadRelatedData failed', e))
  } catch (e: any) {
    console.error('Error loading reports:', e)
    error.value = e?.response?.data?.message || e?.message || 'Failed to load reports'
  } finally {
    loading.value = false
  }
}

// Pagination handlers
const handlePageChange = async (page: number) => {
  currentPage.value = page
  await loadReports(page)
}

const handlePerPageChange = async (size: number) => {
  perPage.value = size
  currentPage.value = 1
  await loadReports(1)
}

// Load user and vehicle details
const loadRelatedData = async () => {
  if (!reportsData.value?.data) return
  const allReports = [...reportsData.value.data.active, ...reportsData.value.data.resolved]

  const userIds = [...new Set(allReports.map(r => r.user_id).filter(Boolean))]
    .filter(id => !users.value[id])
  const vehicleIds = [...new Set(allReports.map(r => r.ev_car_profile_id).filter(Boolean))]
    .filter(id => !vehicles.value[id])

  await Promise.all([
    // краще: один батч-запит
    Promise.all(userIds.map(async (id) => {
      try { users.value[id] = (await getUserDetails(id)).data.data } catch (e) { console.error(e) }
    })),
    Promise.all(vehicleIds.map(async (id) => {
      try {
        const res = await getEvCarProfileById(id)
        vehicles.value[id] = res?.data?.data ?? res?.data ?? res
      } catch (e) { console.error(e) }
    }))
  ])
}

// Function to update active reports count in menu
const updateActiveReportsCount = () => {
  // Emit event to parent component to update the count
  const event = new CustomEvent('updateActiveReportsCount', {
    detail: { count: reportsData.value?.meta?.active?.total || 0 }
  })
  window.dispatchEvent(event)
}

// Mark report as resolved
const markResolved = async (reportId: string) => {
  try {
    resolvingReports.value.push(reportId)

    await changeStatusReport(reportId)

    // Update local state
    if (reportsData.value?.data) {
      const report = reportsData.value.data.active.find(r => r.id === reportId)
      if (report) {
        report.status = 'resolved'
        report.updated_at = new Date().toISOString()

        // Move from active to resolved
        reportsData.value.data.active = reportsData.value.data.active.filter(r => r.id !== reportId)
        reportsData.value.data.resolved.push(report)
        reportsData.value.data.active_count--

        // Update meta
        if (reportsData.value.meta) {
          reportsData.value.meta.active.total--
          reportsData.value.meta.resolved.total++
        }

        // Update active reports count in menu
        updateActiveReportsCount()

        ElMessage.success('Report marked as resolved successfully')
      }
    }

  } catch (e: any) {
    console.error('Error marking report as resolved:', e)
    ElMessage.error('Failed to mark report as resolved')
  } finally {
    resolvingReports.value = resolvingReports.value.filter(id => id !== reportId)
  }
}

// Format date
const formatDate = (dateString: string): string => {
  return dayjs(dateString).format('YYYY-MM-DD HH:mm')
}

// Load on mount
onMounted(() => {
  loadReports()
})

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_skeleton = _resolveComponent("el-skeleton")!
  const _component_el_table_column = _resolveComponent("el-table-column")!
  const _component_router_link = _resolveComponent("router-link")!
  const _component_el_image = _resolveComponent("el-image")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_table = _resolveComponent("el-table")!
  const _component_el_pagination = _resolveComponent("el-pagination")!
  const _component_el_empty = _resolveComponent("el-empty")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("h2", null, "Damage Reports", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_component_el_tag, {
          type: "danger",
          size: "large"
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Active: " + _toDisplayString(meta.value?.active.total || 0), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_el_tag, {
          type: "success",
          size: "large"
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Resolved: " + _toDisplayString(meta.value?.resolved.total || 0), 1)
          ]),
          _: 1
        }),
        _createVNode(_component_el_tag, {
          type: "info",
          size: "large"
        }, {
          default: _withCtx(() => [
            _createTextVNode(" Total: " + _toDisplayString(meta.value?.total || 0), 1)
          ]),
          _: 1
        })
      ])
    ]),
    (error.value)
      ? (_openBlock(), _createBlock(CommonError, {
          key: 0,
          errorMessage: error.value,
          onRetryPress: loadReports
        }, null, 8, ["errorMessage"]))
      : _createCommentVNode("", true),
    (loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_component_el_skeleton, {
            rows: 5,
            animated: ""
          })
        ]))
      : (!loading.value && reports.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (activeReports.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createElementVNode("h3", _hoisted_7, " Active Reports (" + _toDisplayString(activeReports.value.length) + ") ", 1),
                  _createVNode(_component_el_table, {
                    data: activeReports.value,
                    style: {"width":"100%"},
                    class: "reports-table"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_table_column, {
                        prop: "created_at",
                        label: "Created At",
                        width: "180"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createTextVNode(_toDisplayString(formatDate(row.created_at)), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        prop: "description",
                        label: "Description",
                        "min-width": "200"
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "Vehicle",
                        width: "150"
                      }, {
                        default: _withCtx(({ row }) => [
                          (vehicles.value[row.ev_car_profile_id])
                            ? (_openBlock(), _createBlock(_component_router_link, {
                                key: 0,
                                to: {
                    name: _unref(routeNames).evVehicleDetails,
                    params: { id: row.ev_car_profile_id },
                  },
                                class: "vehicle-link"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(vehicles.value[row.ev_car_profile_id].model_name || 'Unknown'), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode("Loading...")
                              ], 64))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "User",
                        width: "200"
                      }, {
                        default: _withCtx(({ row }) => [
                          (users.value[row.user_id])
                            ? (_openBlock(), _createBlock(_component_router_link, {
                                key: 0,
                                to: {
                    name: _unref(routeNames).evUserDetails,
                    params: { id: row.user_id },
                  },
                                class: "user-link"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(users.value[row.user_id].first_name) + " " + _toDisplayString(users.value[row.user_id].last_name), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode("Loading...")
                              ], 64))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "Images",
                        width: "120"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createElementVNode("div", _hoisted_8, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.images.slice(0, 3), (img) => {
                              return (_openBlock(), _createBlock(_component_el_image, {
                                key: img.public_id,
                                src: img.secure_url,
                                "preview-src-list": row.images.map((i) => i.secure_url),
                                style: {"width":"40px","height":"40px","margin-right":"4px"},
                                fit: "cover"
                              }, null, 8, ["src", "preview-src-list"]))
                            }), 128)),
                            (row.images.length > 3)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_9, " +" + _toDisplayString(row.images.length - 3), 1))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "Actions",
                        width: "120"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createVNode(_component_el_button, {
                            type: "success",
                            size: "small",
                            onClick: ($event: any) => (markResolved(row.id)),
                            loading: resolvingReports.value.includes(row.id)
                          }, {
                            default: _withCtx(() => _cache[2] || (_cache[2] = [
                              _createTextVNode(" Resolve ")
                            ])),
                            _: 2,
                            __: [2]
                          }, 1032, ["onClick", "loading"])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["data"])
                ]))
              : _createCommentVNode("", true),
            (resolvedReports.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                  _createElementVNode("h3", _hoisted_11, " Resolved Reports (" + _toDisplayString(resolvedReports.value.length) + ") ", 1),
                  _createVNode(_component_el_table, {
                    data: resolvedReports.value,
                    style: {"width":"100%"},
                    class: "reports-table"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_el_table_column, {
                        prop: "created_at",
                        label: "Created At",
                        width: "180"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createTextVNode(_toDisplayString(formatDate(row.created_at)), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        prop: "updated_at",
                        label: "Resolved At",
                        width: "180"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createTextVNode(_toDisplayString(formatDate(row.updated_at)), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        prop: "status",
                        label: "Status",
                        width: "120"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createVNode(_component_el_tag, {
                            type: "success",
                            size: "small"
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(row.status), 1)
                            ]),
                            _: 2
                          }, 1024)
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        prop: "description",
                        label: "Description",
                        "min-width": "150",
                        "max-width": "200"
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "Vehicle",
                        width: "150"
                      }, {
                        default: _withCtx(({ row }) => [
                          (vehicles.value[row.ev_car_profile_id])
                            ? (_openBlock(), _createBlock(_component_router_link, {
                                key: 0,
                                to: {
                    name: _unref(routeNames).evVehicleDetails,
                    params: { id: row.ev_car_profile_id },
                  },
                                class: "vehicle-link"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(vehicles.value[row.ev_car_profile_id].model_name || 'Unknown'), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode("Loading...")
                              ], 64))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "User",
                        width: "200"
                      }, {
                        default: _withCtx(({ row }) => [
                          (users.value[row.user_id])
                            ? (_openBlock(), _createBlock(_component_router_link, {
                                key: 0,
                                to: {
                    name: _unref(routeNames).evUserDetails,
                    params: { id: row.user_id },
                  },
                                class: "user-link"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(users.value[row.user_id].first_name) + " " + _toDisplayString(users.value[row.user_id].last_name), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"]))
                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createTextVNode("Loading...")
                              ], 64))
                        ]),
                        _: 1
                      }),
                      _createVNode(_component_el_table_column, {
                        label: "Images",
                        width: "120"
                      }, {
                        default: _withCtx(({ row }) => [
                          _createElementVNode("div", _hoisted_12, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.images.slice(0, 3), (img) => {
                              return (_openBlock(), _createBlock(_component_el_image, {
                                key: img.public_id,
                                src: img.secure_url,
                                "preview-src-list": row.images.map((i) => i.secure_url),
                                style: {"width":"40px","height":"40px","margin-right":"4px"},
                                fit: "cover"
                              }, null, 8, ["src", "preview-src-list"]))
                            }), 128)),
                            (row.images.length > 3)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_13, " +" + _toDisplayString(row.images.length - 3), 1))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  }, 8, ["data"])
                ]))
              : _createCommentVNode("", true)
          ]))
        : _createCommentVNode("", true),
    (totalReports.value > 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
          _createVNode(_component_el_pagination, {
            "current-page": currentPage.value,
            "onUpdate:currentPage": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
            "page-size": perPage.value,
            total: totalReports.value,
            "page-count": totalPages.value,
            layout: "total, sizes, prev, pager, next",
            "page-sizes": [10, 20, 50, 100],
            "hide-on-single-page": false,
            onCurrentChange: handlePageChange,
            onSizeChange: handlePerPageChange,
            background: ""
          }, null, 8, ["current-page", "page-size", "total", "page-count"])
        ]))
      : (!loading.value && !reports.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_15, [
            _createVNode(_component_el_empty, { description: "No damage reports found" })
          ]))
        : _createCommentVNode("", true)
  ]))
}
}

})